import Layout from "@/components/Layout";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { CatalogEntityCard, formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import PermissionDenied from "@/components/PermissionDenied";
import { EmployeeSelect, isActiveEmployee } from "@/components/hr/EmployeeSelect";
import { SalaryPeriodFilterField, SalaryPeriodMonthPicker } from "@/components/hr/SalaryPeriodMonthPicker";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { toast } from "@/components/ui/sonner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useGridPagination } from "@/hooks/useGridPagination";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { formatCurrency } from "@/lib/currencies/currenciesStore";
import { formatAmountCommas } from "@/lib/formatPkrAmount";
import { listEmployees, syncEmployeesFromApi, type EmployeeRecord } from "@/lib/hr/employeesStore";
import {
  cancelSalaryAdjustment,
  createSalaryAdjustments,
  fetchSalaryAdjustmentFormMeta,
  listAllSalaryAdjustments,
  periodKey,
  periodLabel,
  salaryAdjustmentsApiErrorMessage,
  updateSalaryAdjustment,
  type EmployeeSalaryAdjustmentRow,
  type SalaryAdjustmentFormMeta,
  type SalaryAdjustmentType,
} from "@/lib/hr/salaryAdjustmentsStore";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { endOfDay, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import {
  ArrowDownLeft,
  ArrowUpRight,
  CalendarRange,
  CheckCircle2,
  Edit,
  Eye,
  Filter,
  LayoutGrid,
  List,
  Loader2,
  Minus,
  Plus,
  Search,
  SlidersHorizontal,
  Trash2,
} from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";

const PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;
const FETCH_CAP = 500;

const MONTHS = [
  { v: 1, label: "January" },
  { v: 2, label: "February" },
  { v: 3, label: "March" },
  { v: 4, label: "April" },
  { v: 5, label: "May" },
  { v: 6, label: "June" },
  { v: 7, label: "July" },
  { v: 8, label: "August" },
  { v: 9, label: "September" },
  { v: 10, label: "October" },
  { v: 11, label: "November" },
  { v: 12, label: "December" },
] as const;

type AdjustmentSortBy = "updated_desc" | "updated_asc" | "period_desc" | "period_asc";

type DraftLine = {
  clientId: string;
  type: SalaryAdjustmentType;
  title: string;
  amount: string;
  periodYear: number;
  periodMonth: number;
  reason: string;
};

type Draft = {
  employeeId: string;
  lines: DraftLine[];
};

function newClientId(): string {
  return typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `line-${Date.now()}-${Math.random()}`;
}

function emptyLine(): DraftLine {
  return {
    clientId: newClientId(),
    type: "addition",
    title: "",
    amount: "",
    periodYear: 0,
    periodMonth: 0,
    reason: "",
  };
}

const emptyDraft = (employeeId = ""): Draft => ({
  employeeId,
  lines: [emptyLine()],
});

function formatMoney(value: string): string {
  const raw = value.replace(/[^\d.]/g, "");
  if (!raw) return value;
  const [whole, ...rest] = raw.split(".");
  const formatted = formatAmountCommas(whole.replace(/\D/g, ""));
  return rest.length ? `${formatted}.${rest.join("")}` : formatted;
}

function safeNumber(v: string): number | null {
  const n = Number(v.replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

function employeeLabel(employees: EmployeeRecord[], employeeId: string, fallbackName?: string | null): string {
  const e = employees.find((x) => x.id === employeeId);
  if (e) return (e.fullName || `${e.firstName} ${e.lastName}`).trim() || e.id;
  if (fallbackName?.trim()) return fallbackName.trim();
  return employeeId || "—";
}

function employeePhoto(employees: EmployeeRecord[], employeeId: string): string | null {
  return employees.find((x) => x.id === employeeId)?.photoDataUrl?.trim() || null;
}

function employeeInitial(name: string): string {
  return name.trim().charAt(0).toUpperCase() || "E";
}

function EmployeeAvatar({ name, photoUrl }: { name: string; photoUrl: string | null }) {
  return (
    <div className="h-9 w-9 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
      {photoUrl ? (
        <img src={photoUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        <span className="text-xs font-bold text-primary">{employeeInitial(name)}</span>
      )}
    </div>
  );
}

function parseAdjMoney(amount: string | number | null | undefined): number | null {
  if (amount == null || amount === "") return null;
  const n = typeof amount === "number" ? amount : Number(String(amount).replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

function employeeBadge(
  employees: EmployeeRecord[],
  employeeId: string,
  fallbackName?: string | null,
): { label: string; className: string } {
  const label = employeeLabel(employees, employeeId, fallbackName);
  const palettes = [
    "bg-sky-500/15 text-sky-700",
    "bg-emerald-500/15 text-emerald-700",
    "bg-violet-500/15 text-violet-700",
    "bg-amber-500/15 text-amber-700",
    "bg-rose-500/15 text-rose-700",
    "bg-teal-500/15 text-teal-700",
  ] as const;
  let h = 0;
  for (let i = 0; i < employeeId.length; i += 1) h = (h * 31 + employeeId.charCodeAt(i)) >>> 0;
  return { label, className: palettes[h % palettes.length] };
}

function typeLabel(type: SalaryAdjustmentType): string {
  return type === "deduction" ? "Deduction" : "Addition";
}

function statusLabel(status: EmployeeSalaryAdjustmentRow["status"]): string {
  if (status === "applied") return "Applied";
  if (status === "cancelled") return "Cancelled";
  return "Pending";
}

function statusBadgeVariant(status: EmployeeSalaryAdjustmentRow["status"]): "default" | "secondary" | "outline" | "destructive" {
  if (status === "applied") return "secondary";
  if (status === "cancelled") return "outline";
  return "default";
}

function buildPeriodOptions(
  meta: SalaryAdjustmentFormMeta | null,
): Array<{ year: number; month: number; key: string; label: string }> {
  const blocked = new Set((meta?.blockedPeriods ?? []).map((p) => periodKey(p.year, p.month)));
  const now = new Date();
  const options: Array<{ year: number; month: number; key: string; label: string }> = [];

  // Current year Jan–Dec, then continue through the next calendar year (24 months from Jan).
  const start = new Date(now.getFullYear(), 0, 1);
  for (let offset = 0; offset < 24; offset += 1) {
    const d = new Date(start.getFullYear(), start.getMonth() + offset, 1);
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const key = periodKey(year, month);
    if (blocked.has(key)) continue;
    options.push({ year, month, key, label: periodLabel(year, month, MONTHS) });
  }

  return options;
}

export default function SalaryAdjustments() {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canCreate = can(user, "salary_adjustment_can_create");
  const canCancel = can(user, "salary_adjustment_can_cancel");
  const [isForbidden, setIsForbidden] = useState(false);

  const [empTick, setEmpTick] = useState(0);
  const employees = useMemo(() => listEmployees(), [empTick]);
  const activeEmployees = useMemo(() => employees.filter(isActiveEmployee), [employees]);

  const [rows, setRows] = useState<EmployeeSalaryAdjustmentRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterEmployee, setFilterEmployee] = useState<string>("all");
  const [filterPeriodYear, setFilterPeriodYear] = useState<number | null>(null);
  const [filterPeriodMonth, setFilterPeriodMonth] = useState<number | null>(null);
  const [pickerYear, setPickerYear] = useState(() => new Date().getFullYear());
  const [sortBy, setSortBy] = useState<AdjustmentSortBy>("updated_desc");
  const [quickDate, setQuickDate] = useState<"today" | "week" | "month" | "all">("all");
  const [viewMode, setViewMode] = useState<"grid" | "list">("list");
  const hasPeriodFilter = filterPeriodYear != null && filterPeriodMonth != null;

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft());
  const isEditing = Boolean(editingId);
  const [viewTarget, setViewTarget] = useState<EmployeeSalaryAdjustmentRow | null>(null);

  const [formMeta, setFormMeta] = useState<SalaryAdjustmentFormMeta | null>(null);
  const [metaLoading, setMetaLoading] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<EmployeeSalaryAdjustmentRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [periodPickerLineId, setPeriodPickerLineId] = useState<string | null>(null);

  useEffect(() => {
    void syncEmployeesFromApi()
      .catch(() => {})
      .finally(() => setEmpTick((t) => t + 1));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setIsForbidden(false);
    try {
      const res = await listAllSalaryAdjustments({ page: 1, perPage: FETCH_CAP });
      setRows(res.rows);
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 403) {
        setIsForbidden(true);
        setRows([]);
        return;
      }
      toast.error(salaryAdjustmentsApiErrorMessage(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  useEffect(() => {
    if (!modalOpen || !draft.employeeId) {
      setFormMeta(null);
      return;
    }
    let cancelled = false;
    setMetaLoading(true);
    void fetchSalaryAdjustmentFormMeta(draft.employeeId)
      .then((meta) => {
        if (!cancelled) setFormMeta(meta);
      })
      .catch((e) => {
        if (!cancelled) {
          toast.error(salaryAdjustmentsApiErrorMessage(e));
          setFormMeta(null);
        }
      })
      .finally(() => {
        if (!cancelled) setMetaLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [modalOpen, draft.employeeId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (filterEmployee !== "all" && row.employeeId !== filterEmployee) return false;
      if (hasPeriodFilter) {
        if (row.periodYear !== filterPeriodYear || row.periodMonth !== filterPeriodMonth) return false;
      }
      if (quickDate !== "all") {
        const updated = parseISO(row.updatedAt);
        if (!isValid(updated)) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today"
            ? startOfDay(now)
            : quickDate === "week"
              ? startOfDay(subDays(now, 6))
              : startOfMonth(now);
        if (updated < start || updated > end) return false;
      }
      if (!q) return true;
      const empName = employeeLabel(employees, row.employeeId, row.employeeName).toLowerCase();
      const hay = `${empName} ${row.title} ${row.type} ${row.amount} ${periodLabel(row.periodYear, row.periodMonth, MONTHS)} ${row.status} ${row.reason ?? ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, query, filterEmployee, hasPeriodFilter, filterPeriodYear, filterPeriodMonth, quickDate, employees]);

  const sortedFiltered = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      const aPeriod = periodKey(a.periodYear, a.periodMonth);
      const bPeriod = periodKey(b.periodYear, b.periodMonth);
      switch (sortBy) {
        case "updated_asc":
          return a.updatedAt.localeCompare(b.updatedAt);
        case "period_desc":
          return bPeriod.localeCompare(aPeriod) || b.id.localeCompare(a.id);
        case "period_asc":
          return aPeriod.localeCompare(bPeriod) || a.id.localeCompare(b.id);
        case "updated_desc":
        default:
          return b.updatedAt.localeCompare(a.updatedAt);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount = (filterEmployee !== "all" ? 1 : 0) + (hasPeriodFilter ? 1 : 0);
  const resetFilters = () => {
    setFilterEmployee("all");
    setFilterPeriodYear(null);
    setFilterPeriodMonth(null);
    setPickerYear(new Date().getFullYear());
  };

  const paginationKey = `${query}|${filterEmployee}|${filterPeriodYear}|${filterPeriodMonth}|${quickDate}|${sortBy}`;
  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    paginatedItems,
    totalCount,
    totalPages,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sortedFiltered, paginationKey, {
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: PAGE_SIZE_OPTIONS[0],
  });

  const periodOptions = useMemo(() => {
    const options = buildPeriodOptions(formMeta);
    const line = draft.lines[0];
    if (!isEditing || !line?.periodYear || !line?.periodMonth) return options;
    const key = periodKey(line.periodYear, line.periodMonth);
    if (options.some((opt) => opt.key === key)) return options;
    return [
      { year: line.periodYear, month: line.periodMonth, key, label: periodLabel(line.periodYear, line.periodMonth, MONTHS) },
      ...options,
    ];
  }, [formMeta, isEditing, draft.lines]);

  const openCreate = () => {
    if (!employees.length) {
      toast.error("No employees available.");
      return;
    }
    setEditingId(null);
    setDraft(emptyDraft());
    setModalOpen(true);
  };

  const canMutatePending = (row: EmployeeSalaryAdjustmentRow) =>
    row.status === "pending" && (row.salaryId == null || row.salaryId === "");

  const openEdit = (row: EmployeeSalaryAdjustmentRow) => {
    if (!canMutatePending(row)) {
      toast.error(
        row.salaryId
          ? "This adjustment is linked to a salary process and can no longer be edited."
          : "Only pending adjustments can be edited.",
      );
      return;
    }
    setEditingId(row.id);
    setDraft({
      employeeId: row.employeeId,
      lines: [
        {
          clientId: newClientId(),
          type: row.type,
          title: row.title || "",
          amount: formatMoney(String(row.amount)),
          periodYear: row.periodYear,
          periodMonth: row.periodMonth,
          reason: row.reason || "",
        },
      ],
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setDraft(emptyDraft());
    setFormMeta(null);
    setPeriodPickerLineId(null);
  };

  const updateLine = (clientId: string, patch: Partial<DraftLine>) => {
    setDraft((d) => ({
      ...d,
      lines: d.lines.map((line) => (line.clientId === clientId ? { ...line, ...patch } : line)),
    }));
  };

  const addLine = () => {
    setDraft((d) => ({ ...d, lines: [...d.lines, emptyLine()] }));
  };

  const removeLine = (clientId: string) => {
    setDraft((d) => {
      if (d.lines.length <= 1) return d;
      return { ...d, lines: d.lines.filter((line) => line.clientId !== clientId) };
    });
  };

  const save = async () => {
    if (saving) return;
    if (!draft.employeeId) return toast.error("Select an employee.");
    if (!formMeta) return toast.error("Available periods are still loading.");

    if (isEditing && editingId) {
      const line = draft.lines[0];
      if (!line) return toast.error("Adjustment details are missing.");
      if (!line.title.trim()) return toast.error("Title is required.");
      const amount = safeNumber(line.amount);
      if (!amount || amount <= 0) return toast.error("Amount must be greater than zero.");
      if (!line.periodYear || !line.periodMonth) return toast.error("Select a salary period.");

      setSaving(true);
      try {
        const result = await updateSalaryAdjustment({
          id: editingId,
          employeeId: draft.employeeId,
          type: line.type,
          title: line.title.trim(),
          amount,
          periodYear: line.periodYear,
          periodMonth: line.periodMonth,
          reason: line.reason.trim() || null,
        });
        toast.success(result.message);
        closeModal();
        await load();
      } catch (e) {
        toast.error(salaryAdjustmentsApiErrorMessage(e));
      } finally {
        setSaving(false);
      }
      return;
    }

    const payloadLines: Array<{
      type: SalaryAdjustmentType;
      title: string;
      amount: number;
      periodYear: number;
      periodMonth: number;
      reason: string | null;
    }> = [];

    for (const line of draft.lines) {
      if (!line.title.trim()) return toast.error("Each adjustment needs a title.");
      const amount = safeNumber(line.amount);
      if (!amount || amount <= 0) return toast.error("Each amount must be greater than zero.");
      if (!line.periodYear || !line.periodMonth) return toast.error("Select a salary period for each adjustment line.");
      payloadLines.push({
        type: line.type,
        title: line.title.trim(),
        amount,
        periodYear: line.periodYear,
        periodMonth: line.periodMonth,
        reason: line.reason.trim() || null,
      });
    }

    setSaving(true);
    try {
      const result = await createSalaryAdjustments({ employeeId: draft.employeeId, lines: payloadLines });
      toast.success(result.message);
      closeModal();
      setPage(1);
      await load();
    } catch (e) {
      toast.error(salaryAdjustmentsApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmCancel = async () => {
    if (!cancelTarget || cancelling) return;
    setCancelling(true);
    try {
      const result = await cancelSalaryAdjustment(cancelTarget.id);
      toast.success(result.message);
      setCancelTarget(null);
      setPage(1);
      await load();
    } catch (e) {
      toast.error(salaryAdjustmentsApiErrorMessage(e));
    } finally {
      setCancelling(false);
    }
  };

  return isForbidden ? (
    <PermissionDenied
      title="Salary adjustment access required"
      message="You don’t have permission to view salary adjustments. Ask an administrator to grant salary_adjustment_can_view_any."
    />
  ) : (
    <Layout title="Salary adjustments">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Salary adjustments</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Record additions and deductions by salary period — each line is applied when that period&apos;s payroll is processed.
            </p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" onClick={openCreate} disabled={!employees.length}>
              <Plus className="w-4 h-4" />
              Add salary adjustment
            </Button>
          ) : null}
        </div>

        <Dialog open={modalOpen} onOpenChange={(o) => (o ? setModalOpen(true) : closeModal())}>
          <DialogContent className="max-w-5xl w-[95vw] max-h-[90vh] overflow-y-auto p-6">
            <DialogHeader className="pb-3 border-b border-border/50">
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-600">
                  <SlidersHorizontal className="w-5 h-5" />
                </div>
                <div>
                  <DialogTitle className="text-lg font-bold text-foreground">
                    {isEditing ? "Edit salary adjustment" : "New salary adjustment"}
                  </DialogTitle>
                  <p className="text-xs text-muted-foreground">
                    {isEditing
                      ? "Update this pending addition or deduction for the selected salary period."
                      : "Add one-time salary additions or deductions for an employee’s payroll period."}
                  </p>
                </div>
              </div>
            </DialogHeader>
            <div className="space-y-4 py-1">
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-foreground">
                  Employee <span className="text-destructive">*</span>
                </Label>
                <EmployeeSelect
                  value={draft.employeeId}
                  onChange={(id) => setDraft((d) => ({ ...d, employeeId: id }))}
                  employees={employees}
                  disabled={isEditing}
                  placeholder="Select employee…"
                  triggerClassName="h-10"
                />
              </div>

              {draft.employeeId && metaLoading ? (
                <div className="rounded-xl border border-border/50 bg-muted/15 px-3 py-2 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Loading available periods…
                  </span>
                </div>
              ) : null}

              <div className="space-y-3 rounded-xl border border-border/50 bg-muted/15 p-3">
                  <div className={cn(
                    "hidden md:grid gap-3 px-1",
                    isEditing
                      ? "md:grid-cols-[minmax(7.5rem,0.85fr)_minmax(8rem,1fr)_minmax(7.5rem,0.9fr)_minmax(10rem,1.1fr)_minmax(10rem,1.2fr)]"
                      : "md:grid-cols-[minmax(7.5rem,0.85fr)_minmax(8rem,1fr)_minmax(7.5rem,0.9fr)_minmax(10rem,1.1fr)_minmax(10rem,1.2fr)_auto]",
                  )}>
                    <Label className="text-xs font-semibold text-foreground">Type</Label>
                    <Label className="text-xs font-semibold text-foreground">Title</Label>
                    <Label className="text-xs font-semibold text-foreground">Amount</Label>
                    <Label className="text-xs font-semibold text-foreground">Salary period</Label>
                    <Label className="text-xs font-semibold text-foreground">Reason</Label>
                    {!isEditing ? <span className="sr-only">Actions</span> : null}
                  </div>

                  {draft.lines.map((line, index) => (
                    <div
                      key={line.clientId}
                      className={cn(
                        "grid grid-cols-1 gap-3 items-end rounded-lg border border-border/50 bg-background/60 p-3 md:border-0 md:bg-transparent md:p-0 md:rounded-none",
                        isEditing
                          ? "md:grid-cols-[minmax(7.5rem,0.85fr)_minmax(8rem,1fr)_minmax(7.5rem,0.9fr)_minmax(10rem,1.1fr)_minmax(10rem,1.2fr)]"
                          : "md:grid-cols-[minmax(7.5rem,0.85fr)_minmax(8rem,1fr)_minmax(7.5rem,0.9fr)_minmax(10rem,1.1fr)_minmax(10rem,1.2fr)_auto]",
                      )}
                    >
                      <div className="space-y-2">
                        {index === 0 ? <Label className="text-xs font-semibold text-foreground md:sr-only">Type</Label> : null}
                        <Select
                          value={line.type}
                          onValueChange={(v) => updateLine(line.clientId, { type: v as SalaryAdjustmentType })}
                        >
                          <SelectTrigger className="glass-input h-10">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="addition">Addition</SelectItem>
                            <SelectItem value="deduction">Deduction</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        {index === 0 ? <Label className="text-xs font-semibold text-foreground md:sr-only">Title</Label> : null}
                        <Input
                          className="glass-input h-10"
                          value={line.title}
                          onChange={(e) => updateLine(line.clientId, { title: e.target.value })}
                          placeholder="e.g. Bonus"
                        />
                      </div>
                      <div className="space-y-2">
                        {index === 0 ? <Label className="text-xs font-semibold text-foreground md:sr-only">Amount</Label> : null}
                        <Input
                          className="glass-input h-10"
                          value={line.amount}
                          onChange={(e) => updateLine(line.clientId, { amount: formatMoney(e.target.value) })}
                          placeholder="e.g. 10,000"
                          inputMode="decimal"
                        />
                      </div>
                      <div className="space-y-2">
                        {index === 0 ? <Label className="text-xs font-semibold text-foreground md:sr-only">Salary period</Label> : null}
                        <SalaryPeriodMonthPicker
                          open={periodPickerLineId === line.clientId}
                          onOpenChange={(open) => {
                            if (open) setPeriodPickerLineId(line.clientId);
                            else setPeriodPickerLineId((id) => (id === line.clientId ? null : id));
                          }}
                          selectedYear={line.periodYear || null}
                          selectedMonth={line.periodMonth || null}
                          onSelect={(year, month) => {
                            updateLine(line.clientId, { periodYear: year, periodMonth: month });
                            setPeriodPickerLineId(null);
                          }}
                          availablePeriods={periodOptions}
                          disabled={Boolean(draft.employeeId) && metaLoading}
                          description="Select month and year for this adjustment."
                          emptyMessage="No available salary periods for this employee."
                        />
                      </div>
                      <div className="space-y-2">
                        {index === 0 ? <Label className="text-xs font-semibold text-foreground md:sr-only">Reason</Label> : null}
                        <Input
                          className="glass-input h-10"
                          value={line.reason}
                          onChange={(e) => updateLine(line.clientId, { reason: e.target.value })}
                          placeholder="Optional reason"
                        />
                      </div>
                      {!isEditing ? (
                        <div className="flex items-center gap-2 pb-0.5">
                          {index === draft.lines.length - 1 ? (
                            <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0" onClick={addLine} aria-label="Add another line">
                              <Plus className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-10 w-10 shrink-0 text-destructive"
                              onClick={() => removeLine(line.clientId)}
                              aria-label="Remove line"
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ) : null}
                    </div>
                  ))}
              </div>
            </div>
            <DialogFooter className="pt-4 border-t border-border/50 gap-2">
              <Button variant="outline" onClick={closeModal} disabled={saving}>
                Cancel
              </Button>
              <Button
                className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-5"
                onClick={() => void save()}
                disabled={saving || !draft.employeeId || metaLoading}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {isEditing ? "Updating..." : "Saving..."}
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    {isEditing ? "Update Adjustment" : "Save Adjustment"}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={viewTarget != null}
          onOpenChange={(open) => {
            if (!open) setViewTarget(null);
          }}
        >
          <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto p-6">
            <DialogHeader className="pb-3 border-b border-border/50">
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-600">
                  <SlidersHorizontal className="w-5 h-5" />
                </div>
                <div>
                  <DialogTitle className="text-lg font-bold text-foreground">
                    {viewTarget
                      ? employeeLabel(employees, viewTarget.employeeId, viewTarget.employeeName)
                      : "Adjustment details"}
                  </DialogTitle>
                  <p className="text-xs text-muted-foreground">
                    {viewTarget
                      ? `${periodLabel(viewTarget.periodYear, viewTarget.periodMonth, MONTHS)} · ${typeLabel(viewTarget.type)} · ${formatCurrency(parseAdjMoney(viewTarget.amount))} · ${statusLabel(viewTarget.status)}`
                      : "Salary adjustment details"}
                  </p>
                </div>
              </div>
            </DialogHeader>

            <div className="py-1">
              {!viewTarget ? (
                <div className="py-12 text-center text-sm text-muted-foreground">No adjustment selected.</div>
              ) : (
                <div className="overflow-hidden rounded-xl border border-border/60 shadow-sm">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border/50 bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                        <th className="text-left font-semibold py-2.5 px-3">Title</th>
                        <th className="text-left font-semibold py-2.5 px-3">Type</th>
                        <th className="text-left font-semibold py-2.5 px-3">Period</th>
                        <th className="text-left font-semibold py-2.5 px-3">Status</th>
                        <th className="text-left font-semibold py-2.5 px-3">Reason</th>
                        <th className="text-right font-semibold py-2.5 px-3">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-border/40 last:border-0">
                        <td className="py-2.5 px-3 font-medium text-foreground">{viewTarget.title || "—"}</td>
                        <td className="py-2.5 px-3 text-muted-foreground">{typeLabel(viewTarget.type)}</td>
                        <td className="py-2.5 px-3 text-muted-foreground">
                          {periodLabel(viewTarget.periodYear, viewTarget.periodMonth, MONTHS)}
                        </td>
                        <td className="py-2.5 px-3">
                          <Badge variant={statusBadgeVariant(viewTarget.status)}>{statusLabel(viewTarget.status)}</Badge>
                        </td>
                        <td className="py-2.5 px-3 text-muted-foreground max-w-[14rem]">
                          <span className="line-clamp-2" title={viewTarget.reason ?? undefined}>
                            {viewTarget.reason?.trim() || "—"}
                          </span>
                        </td>
                        <td
                          className={cn(
                            "py-2.5 px-3 text-right font-semibold tabular-nums",
                            viewTarget.type === "deduction"
                              ? "text-rose-700 dark:text-rose-400"
                              : "text-emerald-700 dark:text-emerald-400",
                          )}
                        >
                          {viewTarget.type === "deduction" ? "−" : "+"}
                          {formatCurrency(parseAdjMoney(viewTarget.amount))}
                        </td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr className="bg-muted/30 font-semibold">
                        <td className="py-2.5 px-3" colSpan={5}>
                          Total
                        </td>
                        <td
                          className={cn(
                            "py-2.5 px-3 text-right tabular-nums",
                            viewTarget.type === "deduction"
                              ? "text-rose-700 dark:text-rose-400"
                              : "text-emerald-700 dark:text-emerald-400",
                          )}
                        >
                          {viewTarget.type === "deduction" ? "−" : "+"}
                          {formatCurrency(parseAdjMoney(viewTarget.amount))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            <DialogFooter className="pt-4 border-t border-border/50 gap-2">
              <Button variant="outline" onClick={() => setViewTarget(null)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ConfirmAlertDialog
          open={!!cancelTarget}
          onOpenChange={(o) => !o && setCancelTarget(null)}
          title="Cancel salary adjustment?"
          description={
            cancelTarget
              ? `This will cancel “${cancelTarget.title}” (${typeLabel(cancelTarget.type).toLowerCase()} · ${formatAmountCommas(cancelTarget.amount)}) for ${employeeLabel(employees, cancelTarget.employeeId, cancelTarget.employeeName)}.`
              : ""
          }
          cancelLabel="Keep"
          confirmLabel={cancelling ? "Cancelling..." : "Cancel adjustment"}
          onConfirm={() => void confirmCancel()}
        />

        <div className="glass-card p-6 space-y-4">
          <div className="space-y-1.5">
            <Label>Search</Label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  className="glass-input pl-9 h-10"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search employee, title, amount, period…"
                />
              </div>
              <Sheet
                open={filtersOpen}
                onOpenChange={(open) => {
                  setFiltersOpen(open);
                  if (open && filterPeriodYear != null) {
                    setPickerYear(filterPeriodYear);
                  }
                }}
              >
                <SheetTrigger asChild>
                  <Button type="button" variant="glass" className="gap-2 shrink-0">
                    <Filter className="w-4 h-4" />
                    Filters
                    {appliedFiltersCount > 0 ? (
                      <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                        {appliedFiltersCount}
                      </span>
                    ) : null}
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle>Filter salary adjustments</SheetTitle>
                    <SheetDescription>Refine by employee and salary period.</SheetDescription>
                  </SheetHeader>
                  <div className="mt-6 space-y-5">
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Employee</Label>
                      <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All employees</SelectItem>
                          {activeEmployees.map((e) => (
                            <SelectItem key={e.id} value={e.id}>
                              {employeeLabel(employees, e.id)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <SalaryPeriodFilterField
                      selectedYear={filterPeriodYear}
                      selectedMonth={filterPeriodMonth}
                      onChange={(year, month) => {
                        setFilterPeriodYear(year);
                        setFilterPeriodMonth(month);
                      }}
                      browseYear={pickerYear}
                      onBrowseYearChange={setPickerYear}
                    />
                  </div>
                  <SheetFooter className="mt-6 gap-2">
                    <Button type="button" variant="glass" className="w-full" onClick={resetFilters}>
                      Reset
                    </Button>
                    <SheetClose asChild>
                      <Button type="button" variant="premium" className="w-full">
                        Apply filters
                      </Button>
                    </SheetClose>
                  </SheetFooter>
                </SheetContent>
              </Sheet>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <div className="shrink-0">
              <GridPerPageSelect id="adjustment-page-size" pageSize={pageSize} onPageSizeChange={(n) => setPageSize(n as never)} options={PAGE_SIZE_OPTIONS} />
            </div>
            <div className="flex items-center justify-end gap-2 flex-wrap">
              <div className="w-[190px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as AdjustmentSortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="updated_desc">Newest first</SelectItem>
                    <SelectItem value="updated_asc">Oldest first</SelectItem>
                    <SelectItem value="period_desc">Period: Newest first</SelectItem>
                    <SelectItem value="period_asc">Period: Oldest first</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today" },
                    { id: "week" as const, label: "Week" },
                    { id: "month" as const, label: "Month" },
                    { id: "all" as const, label: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id ? "bg-primary/10 text-foreground font-medium" : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button type="button" onClick={() => setViewMode("grid")} className={cn("h-full px-3 text-sm transition-colors", viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50")} aria-label="Grid view">
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => setViewMode("list")} className={cn("h-full px-3 text-sm transition-colors", viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50")} aria-label="List view">
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading…
            </div>
          ) : null}

          {!loading && sortedFiltered.length === 0 ? (
            <div className="text-sm text-muted-foreground">No salary adjustments found for these filters.</div>
          ) : null}

          {!loading && sortedFiltered.length > 0 && viewMode === "grid" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {paginatedItems.map((row, i) => {
                const TypeIcon = row.type === "deduction" ? ArrowDownLeft : ArrowUpRight;
                return (
                  <motion.div key={row.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                    <CatalogEntityCard
                      title={row.title || "Adjustment"}
                      avatarUrl={employeePhoto(employees, row.employeeId)}
                      titleIcon={row.type === "deduction" ? ArrowDownLeft : ArrowUpRight}
                      statusBadge={employeeBadge(employees, row.employeeId, row.employeeName)}
                      primaryMeta={{
                        label: "Period",
                        value: periodLabel(row.periodYear, row.periodMonth, MONTHS),
                        icon: CalendarRange,
                        iconClassName: "text-sky-600",
                      }}
                      secondaryMeta={{
                        label: "Type",
                        value: `${typeLabel(row.type)} · ${formatCurrency(parseAdjMoney(row.amount))}`,
                        icon: TypeIcon,
                        iconClassName: row.type === "deduction" ? "text-rose-600" : "text-emerald-600",
                      }}
                      footer={`Status: ${statusLabel(row.status)} · Updated ${formatCatalogAuditDate(row.updatedAt)}`}
                      footerRight={formatCurrency(parseAdjMoney(row.amount))}
                      footerRightClassName={cn(
                        "text-sm font-bold tabular-nums",
                        row.type === "deduction" ? "text-rose-700 dark:text-rose-400" : "text-emerald-700 dark:text-emerald-400",
                      )}
                      onView={() => setViewTarget(row)}
                      viewIconOnly
                      onEdit={canCreate && canMutatePending(row) ? () => openEdit(row) : undefined}
                      onDelete={canCancel && canMutatePending(row) ? () => setCancelTarget(row) : undefined}
                    />
                  </motion.div>
                );
              })}
            </div>
          ) : null}

          {!loading && sortedFiltered.length > 0 && viewMode === "list" ? (
            <div className="space-y-2">
              {paginatedItems.map((row, i) => {
                const name = employeeLabel(employees, row.employeeId, row.employeeName);
                const photo = employeePhoto(employees, row.employeeId);
                const metaLine = [
                  typeLabel(row.type),
                  periodLabel(row.periodYear, row.periodMonth, MONTHS),
                  statusLabel(row.status),
                  row.title || null,
                  row.reason || null,
                ]
                  .filter(Boolean)
                  .join(" · ");
                const actionBtnClass =
                  "h-8 w-8 p-0 border-border/60 hover:border-primary/50 hover:bg-primary/10 hover:text-primary transition-colors";
                return (
                  <motion.div
                    key={row.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.015 }}
                    className="glass-card px-3 py-2.5"
                  >
                    <div className="flex items-center gap-2.5">
                      <EmployeeAvatar name={name} photoUrl={photo} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center justify-between gap-3 min-w-0 flex-1">
                            <div className="min-w-0 flex-1">
                              <h3 className="text-sm font-semibold text-foreground truncate min-w-0">
                                {name}
                              </h3>
                              <p className="mt-0.5 truncate text-xs text-muted-foreground" title={metaLine}>
                                {metaLine}
                              </p>
                            </div>
                            <span
                              className={cn(
                                "text-sm font-bold tabular-nums shrink-0 text-right",
                                row.type === "deduction"
                                  ? "text-rose-700 dark:text-rose-400"
                                  : "text-emerald-700 dark:text-emerald-400",
                              )}
                            >
                              {row.type === "deduction" ? "−" : "+"}
                              {formatCurrency(parseAdjMoney(row.amount))}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0 pt-0.5">
                            <Tooltip delayDuration={200}>
                              <TooltipTrigger asChild>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className={actionBtnClass}
                                  onClick={() => setViewTarget(row)}
                                  aria-label="View adjustment details"
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top">View details</TooltipContent>
                            </Tooltip>
                            {canCreate && canMutatePending(row) ? (
                              <Tooltip delayDuration={200}>
                                <TooltipTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className={actionBtnClass}
                                    onClick={() => openEdit(row)}
                                    aria-label="Edit adjustment"
                                  >
                                    <Edit className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top">Edit</TooltipContent>
                              </Tooltip>
                            ) : null}
                            {canCancel && canMutatePending(row) ? (
                              <Tooltip delayDuration={200}>
                                <TooltipTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-8 w-8 p-0 border-destructive/40 text-destructive hover:border-destructive/60 hover:bg-destructive/10 hover:text-destructive transition-colors"
                                    onClick={() => setCancelTarget(row)}
                                    aria-label="Cancel adjustment"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top">Cancel</TooltipContent>
                              </Tooltip>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : null}

          {!loading && sortedFiltered.length > 0 ? (
            <GridPaginationFooter page={page} totalPages={totalPages} totalCount={totalCount} rangeStart={rangeStart} rangeEnd={rangeEnd} onPageChange={setPage} />
          ) : null}
        </div>
      </div>
    </Layout>
  );
}
